// libs
import React, { useCallback, useEffect, useRef, useState } from 'react'

// helpers
import { useAuthLogin } from '@lopo-tech/lopo-api'
import { Button } from 'src/components/button/button'
import { CLASS_PREFIX } from 'src/constants/'
import { useIsBlogProfileFeatureDisabled } from 'src/hooks/useIsBlogProfileFeatureDisabled'
import { textResources } from 'src/lang/de'
import { AUTH_CLS } from '../../auth/constants'
import { RegistrationForm } from '../registration_form/registration_form'
import { InitialAuthFooter } from './initial_auth_footer'
import './initial_auth_ui.scss'
import { LoginForm } from './login_form'
import { RegisterOptions } from './register_options'

type ITab = 'login' | 'register'

const cls = `${CLASS_PREFIX}initial_auth_ui`

type Props = {
  closeModal: () => void
  closeable?: boolean
  mode?: 'full' | 'login-only'
  initialTab?: 'login' | 'register'
  onSelectPrivateProfile: () => void
  onSelectPublicProfile: () => void
  onForgotPassword: () => void
  onRegistrationNext: (email: string) => void
  /** Prefills the login field when this view is opened straight from a failed sign-in. */
  initialLoginEmail?: string
  /**
   * Set by a host that reaches the login form by remounting this component. swapMode carries
   * focus for the in-place swap; a remount has nobody to carry it, so focus would land on
   * <body>. With this both hosts leave the user in the same place.
   */
  focusTabOnMount?: boolean
}

export const InitialAuthUI: React.FC<Props> = ({ closeModal, closeable = true, mode = 'full', initialTab = 'login', onSelectPrivateProfile, onSelectPublicProfile, onForgotPassword, onRegistrationNext, initialLoginEmail, focusTabOnMount }) => {
  const [activeTab, setActiveTab] = useState<ITab>(initialTab)
  const [loginEmail, setLoginEmail] = useState(initialLoginEmail ?? '')
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [didLogout, setDidLogout] = useState(false)
  const { isAuthenticated, logout } = useAuthLogin()
  const blogProfileCreationFeatureDisabled = useIsBlogProfileFeatureDisabled()
  const tabRefs = useRef<Record<ITab, HTMLButtonElement | null>>({ login: null, register: null })

  // Mount only: the host that remounts this component to reach the login form has no
  // other way to place focus, and re-running this on every render would steal it back
  // from whatever the user tabbed to.
  useEffect(() => {
    if (focusTabOnMount) tabRefs.current[activeTab]?.focus()
    // The empty array is the point and not an oversight: this must not re-run.
  }, [])

  // Swapping the tab from inside the registration form's footer slot
  // unmounts the button that was just pressed, which would drop focus
  // to <body>. Move focus onto the now-active tab instead.
  const swapMode = useCallback((tab: ITab) => {
    setActiveTab(tab)
    tabRefs.current[tab]?.focus()
  }, [])

  const handleLogout = useCallback(() => {
    setIsLoggingOut(true)
    logout().then(() => {
      setDidLogout(true)
    }).finally(() => {
      setIsLoggingOut(false)
    })
  }, [logout])

  if (isAuthenticated && !didLogout) {
    return (
      <div className={cls}>
        <div className={`${cls}__logged-in`}>
          <div>
            <div className={`${AUTH_CLS}__header`}>{textResources.authentication.alreadyLoggedInTitle}</div>
            <div className={`${AUTH_CLS}__subheader`}>
              {textResources.authentication.alreadyLoggedInDescription}
            </div>
          </div>
          <Button
            size='xlarge'
            variant="secondary"
            title={textResources.shared.logout}
            disabled={isLoggingOut}
            onClick={handleLogout}
          />
        </div>
        <button className={`${cls}__close`} onClick={closeModal}>{textResources.shared.close}</button>
      </div>
    )
  }

  const authFooter = <InitialAuthFooter mode={activeTab} setMode={swapMode} closeModal={closeModal} closeable={closeable} />

  return (
    <div className={cls}>
      {mode === 'full' && (
        <div className={`${cls}__tabs`}>
          <button
            ref={(el) => { tabRefs.current.login = el }}
            className={`${cls}__tab${activeTab === 'login' ? ` ${cls}__tab--active` : ''}`}
            onClick={() => setActiveTab('login')}
          >
            {textResources.shared.login}
          </button>
          <button
            ref={(el) => { tabRefs.current.register = el }}
            className={`${cls}__tab${activeTab === 'register' ? ` ${cls}__tab--active` : ''}`}
            onClick={() => setActiveTab('register')}
          >
            {textResources.shared.register}
          </button>
        </div>
      )}
      <div className={`${cls}__content`}>
        {activeTab === 'login' || mode === 'login-only'
          ? <>
            <LoginForm closeModal={closeModal} onForgotPassword={onForgotPassword} initialEmail={loginEmail} />
            {authFooter}
          </>
          : blogProfileCreationFeatureDisabled
            // The registration form owns the fixed footer area below its
            // scroll region, so the auth footer moves inside it and stays
            // visible while the form content scrolls.
            ? <RegistrationForm
              onNext={onRegistrationNext}
              showHeader={false}
              footer={authFooter}
              // swapMode rather than setActiveTab: pressing the button unmounts it, and
              // swapMode is the one that moves focus onto the tab instead of dropping it.
              onGoToLogin={(email) => {
                setLoginEmail(email)
                swapMode('login')
              }}
            />
            : <>
              <RegisterOptions onSelectPrivateProfile={onSelectPrivateProfile} onSelectPublicProfile={onSelectPublicProfile} />
              {authFooter}
            </>
        }
      </div>
    </div>
  )
}
